import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';

import { comparerByAlphabet } from '@app/comparers';
import { Department } from '@app/models/department';
import {
    AuthActionTypes,
    AuthActionsUnion
} from '@app/modules/core/actions/auth.actions';
import {
    DepartmentActionTypes,
    DepartmentActionsUnion
} from '@core/actions/department.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';

export interface State extends EntityState<Department> {
    loading: boolean;
    creating: boolean;
    loaded: boolean;
    selectedId: number;
    filteredIds: any[];
}

export const adapter: EntityAdapter<Department> = createEntityAdapter<
    Department
>({
    selectId: (department: Department) => department.id,
    sortComparer: comparerByAlphabet('ASC', 'name')
});

export const INITIAL_STATE: State = adapter.getInitialState({
    loading: false,
    creating: false,
    loaded: false,
    selectedId: null,
    filteredIds: []
});

export function reducer(
    state: State = INITIAL_STATE,
    action: DepartmentActionsUnion | AuthActionsUnion
) {
    switch (action.type) {
        case DepartmentActionTypes.Load:
        case DepartmentActionTypes.updateHierarchyPlanner:
        case DepartmentActionTypes.Download:
            return { ...state, loading: true };

        case DepartmentActionTypes.LoadFail:
        case DepartmentActionTypes.updateHierarchyPlannerFail:
        case DepartmentActionTypes.updateHierarchyPlannerSuccess:
        case DepartmentActionTypes.DownloadSuccess:
        case DepartmentActionTypes.DownloadFail:
            return { ...state, loading: false };

        case DepartmentActionTypes.LoadSuccess:
            return adapter.addAll(action.departments, {
                ...state,
                loading: false,
                loaded: true
            });

        case DepartmentActionTypes.LoadByConditions:
            return { ...state, loading: true };

        case DepartmentActionTypes.LoadByConditionsSuccess:
            return adapter.addAll(action.departments, {
                ...state,
                loading: false,
                loaded: true
            });

        case DepartmentActionTypes.LoadByConditionsFail:
            return { ...state, loading: false };

        case DepartmentActionTypes.Create:
            return { ...state, creating: true };

        case DepartmentActionTypes.CreateFail:
            return { ...state, creating: false };

        case DepartmentActionTypes.CreateSuccess:
            return adapter.addOne(action.department, {
                ...state,
                creating: false
            });

        case DepartmentActionTypes.Select:
            return { ...state, selectedId: action.id };

        // KEEP DEPARTMENTS LOADED ON LOGOUT
        case AuthActionTypes.Logout:
            return { ...state, selectedId: null };

        case DepartmentActionTypes.Search:
            return {
                ...state,
                filteredIds: [],
                loading: true
            };

        case DepartmentActionTypes.SearchSuccess:
            return {
                ...state,
                filteredIds:
                    action.DepartmentIds.length === 0 ? [-1] : action.DepartmentIds,
                loading: false
            };

        case DepartmentActionTypes.SearchFail:
            return {
                ...state,
                filteredIds: [],
                loading: false
            };

        case DepartmentActionTypes.ClearSearch:
            return {
                ...state,
                filteredIds: [],
                loading: false
            };

        case DepartmentActionTypes.LoadById:
            return { ...state, loading: true };

        case DepartmentActionTypes.LoadByIdSuccess:
            let newFiltered = []
            if (state.filteredIds.length > 0) {

                let newData = state.filteredIds.filter(item => item.id === action.CategoryId)[0] = {
                    id: action.CategoryId,
                    ...action.data
                }
                newFiltered.push(newData)
                let currentData = state.filteredIds.filter(item => item.id !== action.CategoryId)
                newFiltered.push(...currentData)
            }

            return adapter.updateOne({ id: action.CategoryId, changes: action.data },
                {
                    ...state,
                    filteredIds: newFiltered
                })

        case DepartmentActionTypes.LoadByIdFail:
            return { ...state, loading: false };

        case DepartmentActionTypes.Clear:
            return INITIAL_STATE;

        case DepartmentActionTypes.DeleteSuccess:
            const DepartmentId = action.DepartmentId;

            return adapter.removeOne(DepartmentId, state);

        case DepartmentActionTypes.UpdateSuccess:
            return adapter.upsertOne(action.department, state);
        default:
            return state;
    }
}

export const {
    selectIds,
    selectEntities,
    selectAll,
    selectTotal
} = adapter.getSelectors();

export const selectLoaded = (state: State) => state.loaded;
export const selectLoading = (state: State) => state.loading;
export const selectSelectedId = (state: State) => state.selectedId;
export const getState = createFeatureSelector<State>('departments');
export const selectDepartmentIds = (state: State) => state.filteredIds;

export const getFilteredDepartmentIds = createSelector(
    getState,
    selectDepartmentIds
);
